import DOMPurify from 'dompurify';
import { validateZipCode, validateEmail, validatePhone, handleUrlXss, getRecaptchaToken, getCsrfTokenFromApi } from '../utils';
import ModAccordion from './ModAccordion';
import FileUpload from './FileUpload';

export default class FormSignUp {
  constructor(el) {
    this.$el = $(el);
    this.classes = {
      isInvalid: 'invalid',
      required: 'required',
      disabled: 'btn-disabled',
      hidden: 'hidden',
      submitValid: 'submit-valid',
    };
    this.elementClasses = {
      groupCheckbox: '.group-checkbox',
      selectAllCheckboxes: '.js-select-all-checkboxes',
      selectCheckboxes: '.js-select-checkboxes',
      checkboxes: '.js-checkbox',
      zipCode: '.js-zipcode',
      jsPhone: '.js-phone',
      groupCheckboxMessage: '.group-checkbox-message',
      groupInputMessage: '.group-input-message',
      checkBoxSelect: '.js-select-all-checkboxes, .js-select-checkboxes, .js-checkbox, .js-agree',
      formControl: '.form-control',
      afterSubmit: '.after-submit',
    };
    this.dom = {
      $selectAllCheckboxes: this.$el.find(this.elementClasses.selectAllCheckboxes),
      $selectGroupCheckboxes: this.$el.find(this.elementClasses.selectCheckboxes),
      $checkboxes: this.$el.find(this.elementClasses.checkboxes),
      $zipCode: this.$el.find(this.elementClasses.zipCode),
      $agree: this.$el.find('.js-agree'),
      $jsPhone: this.$el.find('.js-phone'),
      $buttonSubmit: this.$el.find('.js-submit'),
      $loading: this.$el.find('.js-loading'),
      $wrapperForm: this.$el.find('.form-wrapper'),
      $parent: this.$el.closest('.footer, .parent-form').length ? this.$el.closest('.footer, .parent-form') : this.$el,
      $finishForm: this.$el.find('.js-finish-form'),
      $accordion: this.$el.closest('.footer-sign-up'),
      $unsubscribe: this.$el.find('.js-unsubscribe'),
    };
    this.checked = 'checked';
    this.submitReady = false;
    this.thankYouPage = this.$el.data('url');
    this.action = this.$el.attr('action');
    this.method = this.$el.attr('method');
    this.submitting = false;
    this.modAccordion = new ModAccordion(this.$el.closest('.footer-sign-up'));
    this.fileUpload = null;
    this.init();
  }

  init() {
    this.modAccordion.init();
    this.setUpValidation();
    this.agreeChange();
    this.addEventListeners();
    const $fileUploadSection = this.$el.find('.form-group-v2:has(#file-upload)');
    if ($fileUploadSection.length) {
      this.fileUpload = new FileUpload($fileUploadSection);
    }
  }

  addEventListeners() {
    this.dom.$selectAllCheckboxes.on('change', this.selectAllCheckboxesChange.bind(this));
    this.dom.$checkboxes.on('change', this.checkboxesChange.bind(this));
    this.dom.$selectGroupCheckboxes.on('change', this.selectGroupCheckboxesChange.bind(this));
    this.dom.$agree.on('change', this.agreeChange.bind(this));
    this.$el.find('input, textarea').on('keyup focusout', this.inputFocus.bind(this));
    this.$el.on('submit', this.formSubmit.bind(this));
    this.dom.$finishForm.on('click', this.openForm.bind(this));
    this.dom.$unsubscribe.on('click', this.unSubscribe.bind(this));
    this.$el.find(this.elementClasses.zipCode).on('input keydown', this.zipCodeInputHandler.bind(this));
  }

  unSubscribe(e) {
    e.preventDefault();
    this.$el.find(this.elementClasses.checkBoxSelect).not('.js-agree').prop('checked', false);
    this.$el.find(this.formControl).val('');
    this.submitReady = true;
    this.$el.submit();
  }

  openForm() {
    this.dom.$wrapperForm.removeClass(this.classes.hidden);
    this.$el.find(this.elementClasses.afterSubmit).addClass(this.classes.hidden);
  }

  selectAllCheckboxesChange() {
    const isChecked = this.dom.$selectAllCheckboxes.prop(this.checked);
    this.dom.$checkboxes.prop(this.checked, isChecked);
    this.dom.$selectGroupCheckboxes.prop(this.checked, isChecked);
  }

  checkboxesChange(e) {
    const $group = $(e.currentTarget).closest(this.elementClasses.groupCheckbox);
    const allChecked = this.dom.$checkboxes.length === this.dom.$checkboxes.filter(':checked').length;
    const groupChecked = $group.find(this.elementClasses.checkboxes).length === $group.find(this.elementClasses.checkboxes).filter(':checked').length;
    this.dom.$selectAllCheckboxes.prop(this.checked, allChecked);
    $group.find(this.elementClasses.selectCheckboxes).prop(this.checked, groupChecked);
  }

  selectGroupCheckboxesChange(e) {
    const $group = $(e.currentTarget).closest(this.elementClasses.groupCheckbox);
    const isChecked = $(e.currentTarget).is(':checked');
    $group.find(this.elementClasses.checkboxes).prop(this.checked, isChecked);
    const allChecked = this.dom.$checkboxes.length === this.dom.$checkboxes.filter(':checked').length;
    this.dom.$selectAllCheckboxes.prop(this.checked, allChecked);
  }

  agreeChange(_e = null) {
    const $agreeCheckboxes = this.dom.$agree;
    let allNonOptionalChecked = true;

    if ($agreeCheckboxes.length) {
      $agreeCheckboxes.each((_index, checkbox) => {
        if (!$(checkbox).is(':checked') && !$(checkbox).hasClass('optional')) {
          allNonOptionalChecked = false;
        }
      });
    }

    if (allNonOptionalChecked) {
      this.dom.$buttonSubmit.removeAttr('disabled').removeClass(this.classes.disabled);
      this.submitReady = true;
    } else {
      this.dom.$buttonSubmit.attr('disabled', 'disabled').addClass(this.classes.disabled);
      this.submitReady = false;
    }
  }

  zipCodeInputHandler(e) {
    const code = e.key;
    if (['e', '-', '+', 'E'].includes(code)) {
      e.preventDefault();
    }
    this.maxLengthZipCode(e);
  }

  maxLengthZipCode(e) {
    if ($(e.currentTarget).val().length >= 5) {
      $(e.currentTarget).val($(e.currentTarget).val().slice(0, 5));
    }
  }

  inputFocus(e) {
    const $input = $(e.currentTarget);
    // Skip validation for file input type on click/focus events
    if ($input.attr('type') !== 'file') {
      this.validateField($input);
    }
  }

  formSubmit(e) {
    e.preventDefault();
    if (this.validateForm() && this.submitReady && this.action && !this.submitting) {
      this.submitting = true;
      this.$el.addClass('submitting');
      this.dom.$loading.removeClass('opacity-0');

      getCsrfTokenFromApi().then((csrfToken) => {
        getRecaptchaToken('submit').then((token) => {
          const formData = new FormData(this.$el[0]);

          formData.append('RecaptchaResponse', token);
          formData.append('__RequestVerificationToken', csrfToken);

          if (this.fileUpload) {
            const files = this.fileUpload.getFiles();
            files.forEach((file, index) => {
              formData.append(`UploadFiles[${index}]`, file);
            });
          }

          $.ajax({
            type: this.method || 'POST',
            url: this.action,
            data: formData,
            processData: false,
            contentType: false,
            success: (response) => {
              this.successSubmit(response);
            },
            error: () => {
              this.errorSubmit('');
            },
          });
        });
      });
    }
  }

  successSubmit(response) {
    this.dom.$loading.addClass('opacity-0');
    this.$el.removeClass('submitting');
    this.submitting = false;
    if (response.success) {
      this.afterSubmit(response);
    } else {
      this.errorSubmit(response.message);
    }
    if (response.deserializeResponseContent) {
      this.$el.find('.after-response').removeClass('hidden');
      this.$el.find('.payload').val(response.payload);
      this.$el.find('.response').val(response.response);
      this.$el.find('.deserializeResponseContent').val(response.deserializeResponseContent);
      this.formatJSON('.payload');
      this.formatJSON('.response');
      this.formatJSON('.deserializeResponseContent');
    }
  }

  formatJSON(element) {
    const $textarea = $(element);
    try {
      const jsonText = $textarea.val();
      if (jsonText && jsonText.trim() !== '') {
        const jsonObj = JSON.parse(jsonText);
        const formattedJson = JSON.stringify(jsonObj, null, 2);
        $textarea.val(formattedJson);
      }
    } catch (error) {
      console.error('Error formatting JSON:', error);
    }
  }

  errorSubmit(message) {
    this.submitting = false;
    const $errorMessages = this.$el.find('.error-message-form');
    const textError = DOMPurify.sanitize(message || 'Something went wrong. Please try again later.');
    if (!$errorMessages.length) {
      const errorHtml = DOMPurify.sanitize(`<div class="error-message-form text-white text-base my-10 uppercase text-center py-2 px-10 font-bold bg-primary-100">${textError}</div>`);
      this.dom.$wrapperForm.prepend(errorHtml);
    } else {
      $errorMessages.text(textError);
    }
    window.scrollAgent.eventScrollTopSection($errorMessages);
  }

  afterSubmit(response) {
    if (response.url || this.thankYouPage) {
      window.location.href = handleUrlXss(response.url || this.thankYouPage);
      return;
    }
    window.scrollAgent.eventScrollTopSection(this.dom.$parent);
    this.dom.$wrapperForm.addClass(this.classes.hidden);
    this.$el.find(this.elementClasses.checkBoxSelect).prop('checked', false);
    this.$el.find(this.elementClasses.formControl).val('');
    this.$el.find(this.elementClasses.afterSubmit).removeClass(this.classes.hidden);
  }

  setUpValidation() {
    this.$el.find('input[required]').addClass(this.classes.required).removeAttr(this.classes.required);
  }

  validateField($input) {
    const isRequired = $input.hasClass(this.classes.required);
    const type = $input.attr('type');
    const $parent = $input.parents('.form-group, .group-checkbox');
    const checkMail = type === 'email' && !validateEmail($input.val());
    const checkZip = $input.is(this.elementClasses.zipCode) && !validateZipCode($input.val());
    const checkPhone = $input.is(this.elementClasses.jsPhone) && !validatePhone($input.val());
    let isValid = true;
    if (isRequired) {
      isValid = this.checkValidateRequire(isValid, $input, checkMail, checkZip, checkPhone);
    } else if ($input.val().trim() !== '') {
      if (checkMail || checkZip || checkPhone) {
        isValid = false;
      }
    } else {
      // something else
    }
    $parent.toggleClass(this.classes.isInvalid, !isValid);
  }

  validateGroupCheckbox() {
    let isValid = true;
    this.$el.find('.validate-group-checkbox').each((_i, e) => {
      const isInValidGroup = $(e).find('input:checked').length;
      if (isInValidGroup) {
        $(e).find(this.elementClasses.groupCheckboxMessage).addClass(this.classes.hidden);
      } else {
        isValid = false;
        $(e).find(this.elementClasses.groupCheckboxMessage).removeClass(this.classes.hidden);
      }
    });
    return isValid;
  }

  checkValidateRequire(isValid, $input, checkMail, checkZip, checkPhone) {
    if ($input.val().trim() === '' || checkMail || checkZip || checkPhone) {
      isValid = false;
    }
    return isValid;
  }

  validateForm() {
    this.$el.find('input:not([type="file"]), textarea').each((_index, input) => {
      this.validateField($(input));
    });

    // Check file upload validation if fileUpload exists
    if (this.fileUpload) {
      const $fileList = this.fileUpload.elements.fileList;
      if ($fileList.children().length === 0 && this.fileUpload.isRequired) {
        this.fileUpload.showError(this.fileUpload.TEXT.REQUIRED_ERROR);
      } else {
        this.fileUpload.hideError();
      }
    }

    const $firstError = this.$el.find(`.${this.classes.isInvalid}`).first().find('input:not([type="file"]), textarea');
    const validateGroupCheckbox = this.validateGroupCheckbox();
    if ($firstError.length) {
      $firstError.focus();
      this.$el.addClass(this.classes.submitValid);
      $(this.elementClasses.groupInputMessage).removeClass(this.classes.hidden);
      $(this.elementClasses.groupCheckboxMessage).addClass(this.classes.hidden);
      window.scrollAgent.eventScrollTopSection($firstError);
    } else if (validateGroupCheckbox) {
      window.scrollAgent.eventScrollTopSection(this.$el.find(`${this.elementClasses.groupCheckboxMessage}:not(.hidden)`).first());
    } else {
      this.$el.removeClass(this.classes.submitValid);
      $(this.elementClasses.groupInputMessage).addClass(this.classes.hidden);
      // something else
    }

    return this.$el.find(`.${this.classes.isInvalid}`).length === 0 && validateGroupCheckbox;
  }
}
